import { Component, OnInit, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, TemplateRef, ChangeDetectionStrategy, inject, output, viewChild, input, signal } from '@angular/core';
import {environment} from '@environments/environment';
import {select, Store} from '@ngrx/store';
import {getToken, IAuthenticationState} from '@app/stores';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import { IframeModalShellComponent } from '../../../_components/iframe-modal-shell/iframe-modal-shell.component';
import { SanitizeHtmlPipe } from '@app/_pipes';

declare let FroalaEditor: any;

/** The current font or size in an open dropdown, in the colour the editor uses for it. */
const CURRENT_ITEM_COLOR = '#4BB7E3';

/**
 * Marks the dropdown items whose title `isCurrent` accepts as Froala marks its active item, and
 * unmarks the rest. Returns whether any was marked.
 */
function markDropdownItems(options: NodeListOf<HTMLLIElement>, isCurrent: (title: string | null) => boolean): boolean {
  let found = false;
  options.forEach(option => {
    const link = option.querySelector('a');
    link.style.removeProperty('color');
    link.style.removeProperty('background');
    link.setAttribute('aria-selected', 'false');
    link.classList.remove('fr-active');
    if (isCurrent(link.getAttribute('title'))) {
      found = true;
      link.setAttribute('aria-selected', 'true');
      link.classList.add('fr-active');
      link.style.color = CURRENT_ITEM_COLOR;
      link.style.background = 'unset';
    }
  });
  return found;
}

/** Scrolls the dropdown so its active item sits one row below the top, or to the top without one. */
function scrollToActiveItem(menu: HTMLElement): void {
  const item = menu.querySelector('.fr-active')?.parentElement;
  let top = 0;
  if (item) {
    const height = parseFloat(window.getComputedStyle(item).height);
    top = Array.prototype.indexOf.call(item.parentElement.children, item) * height - height;
  }
  menu.scrollTo({top, behavior: 'smooth'});
}

@Component({
  selector: 'app-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss', '../../../../assets/css/editor/custom.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IframeModalShellComponent, SanitizeHtmlPipe]
})

export class EditorComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private _modalService = inject(NgbModal);
  private sanitizer = inject(DomSanitizer);

  readonly template = input(undefined);
  readonly customVariables = input<any>(null);
  readonly influencerTypeId = input(undefined);
  readonly allowReordering = input(undefined);
  readonly onChange = input(undefined);
  readonly isEditorLoaded = output<any>();
  readonly templateButtonText = input(undefined);
  readonly templateButtonTextColor = input(undefined);
  readonly templateButtonLink = input(undefined);
  readonly templateButtonSpacing = input(undefined);
  readonly templateParagraphSpacing = input(undefined);
  readonly templateListSpacing = input(undefined);
  readonly templateButtonColor = input(undefined);
  readonly templateLinkColor = input(undefined);
  readonly templateLinkHoverColor = input(undefined);
  readonly templateBackgroundColor = input(undefined);
  readonly variablePickerModal = viewChild<TemplateRef<any>>('variablePickerModal');
  unsubscriber = new Subject();

  public HTMLTemplate;
  public editables = {};
  public imageEditables = {};
  token = null;

  editorLoaded = false;
  editors: any = [];
  imageEditors: any = [];
  timer: any;
  linkColor = '';
  readonly variablePickerUrl = signal<SafeResourceUrl | null>(null);
  private _pendingVariableContext: string;
  private _pendingEditor: any;
  private _pendingImageElement: HTMLImageElement;
  private _pendingImagePopupEl: HTMLElement;
  private _messageListener: any;

  constructor() {
    this.authenticationStore.pipe(select(getToken))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(newToken => {
        if (newToken) {
          this.token = newToken;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.templateButtonText && changes.templateButtonText.currentValue) {
      this.replaceButtonText(changes.templateButtonText.currentValue);
    }
    if (changes.templateButtonTextColor && changes.templateButtonTextColor.currentValue) {
      this.replaceButtonTextColor(changes.templateButtonTextColor.currentValue);
    }
    if (changes.templateButtonLink) {
      this.replaceButtonLink(changes.templateButtonLink.currentValue || '');
    }
    if (changes.templateButtonColor) {
      this.replaceButtonColor(changes.templateButtonColor.currentValue || '');
    }
    if (changes.templateLinkColor) {
      this.replaceLinkColor(changes.templateLinkColor.currentValue || '');
    }
    if (changes.replaceLinkHoverColor) {
      this.replaceLinkHoverColor(changes.replaceLinkHoverColor.currentValue || '');
    }
    if (changes.templateBackgroundColor) {
      this.replaceBackGroundColor(changes.templateBackgroundColor.currentValue || '');
    }
    if (changes.templateButtonSpacing) {
      this.replaceButtonSpacing(changes.templateButtonSpacing.currentValue || '');
    }
    if (changes.templateParagraphSpacing) {
      this.replaceParagraphSpacing(changes.templateParagraphSpacing.currentValue || '');
    }
    // if (changes.template) {
    //   this.initEditor();
    // }
  }

  ngOnInit() {
    this.initEditables();
  }

  customFontFamily() {
    const that = this;
    FroalaEditor.RegisterCommand('fontFamily', {
      type: 'dropdown',
      displaySelection(editor) {
        return editor.opts.fontFamilySelection;
      },
      defaultSelection(editor) {
        return editor.opts.fontFamilyDefaultSelection;
      },
      displaySelectionWidth: 120,
      html() {
        let html = '<ul class="fr-dropdown-list" role="presentation">';
        const fontFamilies = this.opts.fontFamily;
        for (const family in fontFamilies) {
          if (fontFamilies.hasOwnProperty(family)) {
            html += '<li role="presentation"><a class="fr-command" tabIndex="-1" role="option" data-cmd="fontFamily" data-param1="'.concat(fontFamilies[family], '" \n        style="font-family: ').concat(family.replace('google-', ''), '" title="').concat(fontFamilies[family], '">').concat(fontFamilies[family], '</a></li>');
          }
        }
        return html += '</ul>';
      },
      title: 'Font Family',
      callback(cmd, font) {
        setTimeout(() => {
          this.fontFamily.apply(font);
        }, 500);
      },
      refresh(element) {
        this.fontFamily.refresh(element);
      },
      refreshOnShow(element, t) {
        if (t) {
          t[0].style.overflow = 'hidden auto';
        }
        setTimeout(() => {
          const menu: HTMLElement = t?.[0];
          if (!menu) {
            return;
          }
          const options = menu.querySelectorAll('li');
          let beforeFontFamily = '';
          if (FroalaEditor?.SELECTED_TARGET) {
            try {
              beforeFontFamily = window.getComputedStyle(FroalaEditor.SELECTED_TARGET, null).getPropertyValue('font-family');
              if (beforeFontFamily) {
                beforeFontFamily = beforeFontFamily.split('"').join('');
                const found = markDropdownItems(options, title => title === beforeFontFamily);
                if (!found) {
                  const name = beforeFontFamily.charAt(0).toUpperCase() + beforeFontFamily.slice(1);
                  const li = document.createElement('li');
                  li.setAttribute('role', 'presentation');
                  const aCommand = document.createElement('a');
                  aCommand.setAttribute('class', 'fr-command fr-active');
                  aCommand.setAttribute('role', 'option');
                  aCommand.setAttribute('data-cmd', 'fontFamily');
                  aCommand.setAttribute('data-param1', beforeFontFamily);
                  aCommand.setAttribute('title', beforeFontFamily);
                  aCommand.setAttribute('aria-selected', 'true');
                  aCommand.appendChild(document.createTextNode(name));
                  aCommand.style.fontFamily = beforeFontFamily;
                  aCommand.style.color = CURRENT_ITEM_COLOR;
                  aCommand.style.background = 'unset';
                  li.append(aCommand);
                  menu.querySelector('ul').prepend(li);
                }

                scrollToActiveItem(menu);
              }
            } catch (e) {
              console.log('Error in finding current font-family', e);
            }
          }
        }, 1000);
        this.fontFamily.refreshOnShow(element, t);

        /** code here */

      },
      plugin: 'fontFamily'
    });
  }

  customFontSize() {
    const that = this;
    // <li role="presentation"><a class="fr-command" tabindex="-1" role="option" data-cmd="fontSize" data-param1="8px" title="8" aria-selected="false">8</a></li>
    FroalaEditor.RegisterCommand('fontSize', {
      type: 'dropdown',
      title: 'Font Size',
      icon: 'fontSize',
      displaySelection(e) {
        return e.opts.fontSizeSelection;
      },
      displaySelectionWidth: 30,
      defaultSelection(e) {
        return e.opts.fontSizeDefaultSelection;
      },
      html() {
        let e = '<ul class="fr-dropdown-list" role="presentation">';
        const t = this.opts.fontSize;
        for (const r of t) {
          e += '<li role="presentation"><a class="fr-command" tabIndex="-1" role="option" data-cmd="fontSize" data-param1="'.concat(r).concat(this.opts.fontSizeUnit, '" title="').concat(r, '">').concat(r, '</a></li>');
        }
        return e += '</ul>';
      },
      callback(e, t) {
        const element = FroalaEditor.SELECTED_TARGET;
        element.style.setProperty('font-size', t, 'important');
      },
      refresh(e) {
        this.fontSize.refresh(e);
      },
      refreshOnShow(e, t) {
        setTimeout(() => {
          const menu: HTMLElement = t?.[0];
          if (!menu) {
            return;
          }
          const options = menu.querySelectorAll('li');
          let beforeFontSize: any = 0;
          if (FroalaEditor?.SELECTED_TARGET) {
            try {
              beforeFontSize = window.getComputedStyle(FroalaEditor.SELECTED_TARGET, null).getPropertyValue('font-size');
              if (beforeFontSize) {
                beforeFontSize = parseInt(beforeFontSize, 10);
                const found = markDropdownItems(options, title => parseInt(title, 10) === beforeFontSize);
                if (!found) {
                  const arr = [...this.opts.fontSize];
                  let html = '';
                  arr.push(beforeFontSize.toString());
                  arr.sort();
                  for (const r of arr) {
                    html += '<li role="presentation"><a class="fr-command' + (String(r) === String(beforeFontSize) ? ' fr-active' : '')
                        + '" ' + (String(r) === String(beforeFontSize) ? 'aria-selected="true" ' : '')
                        + '" ' + (String(r) === String(beforeFontSize) ? 'style="color: #4BB7E3; background: unset" ' : '')
                        + ' tabIndex="-1" role="option" data-cmd="fontSize" data-param1="'.concat(r).concat(this.opts.fontSizeUnit, '" title="').concat(r, '">').concat(r, '</a></li>');
                  }
                  menu.querySelector('ul').innerHTML = html;
                }

                scrollToActiveItem(menu);
              }
            } catch (e) {
              console.log('Error in finding current font-family', e);
            }
          }
        }, 500);
        this.fontSize.refreshOnShow(e, t);
      },
      plugin: 'fontSize'
    });
  }

  customPaste() {
    const that = this;
    const customPlugin = 'customPaste';
    const customPopupName = 'customPaste.popup';

    // Define popup template.
    Object.assign(FroalaEditor.POPUP_TEMPLATES, {
      [customPopupName]: '[_BUTTONS_][_CUSTOM_LAYER_]'
    });

    // Define popup buttons.
    Object.assign(FroalaEditor.DEFAULTS, {
      pastePopupButtons: ['pastePopupClose', '-']
    });

    FroalaEditor.PLUGINS[customPlugin] = function(editor) {

      let pasteContent = null;
      // Show the popup
      function showPopup(e) {
        pasteContent = e;
        let $popup = editor.popups.get(customPopupName);
        if (!$popup) {
          $popup = initPopup();
        }

        const bodyRect = document.body.getBoundingClientRect();
        const targetElement = e.target.getBoundingClientRect();
        editor.popups.setContainer(customPopupName, editor.$sc);
        const left = targetElement.left;
        const top = (targetElement.top - bodyRect.top) + targetElement.height;
        editor.popups.show(customPopupName, left, top, targetElement.height);
      }

      // Hide the custom popup.
      function hidePopup() {
        editor.popups.hide(customPopupName);
      }

      // Create custom popup.
      function initPopup() {
        // Load popup template.
        let template = FroalaEditor.POPUP_TEMPLATES[customPopupName];
        if (typeof template === 'function') {
          template = template.apply(editor);
        }

        // Popup buttons.
        let popup_buttons = '';

        // Create the list of buttons.
        if (editor.opts.pastePopupButtons.length > 1) {
          popup_buttons += '<div class="fr-buttons fr-tabs">';
          popup_buttons += editor.button.buildList(editor.opts.pastePopupButtons);
          popup_buttons += '</div>';
        }

        // Define Popup Template
        let cleanPasteButton = '<button id="pasteCleanButton-' + editor.id + '" type="button" tabindex="-1" role="button" data-cmd="pasteCleanButton" data-blur-event-set="true" class="fr-command fr-btn pasteCleanButton" style="display: flex;align-items: center;margin-right: 10px;border: 1px solid #00000038; padding: 10px;" data-title="Clean formatting">';
        cleanPasteButton += 'Clear formatting';
        cleanPasteButton += '</button>';

        let pasteKeepFormatButton = '<button id="pasteKeepButton-' + editor.id + '" type="button" tabindex="-1" role="button" data-cmd="pasteKeepButton" data-blur-event-set="true" class="fr-command fr-btn pasteKeepButton" style="display: flex;align-items: center;border: 1px solid #00000038; padding: 10px;" data-title="Keep formatting">';
        pasteKeepFormatButton += 'Keep formatting';
        pasteKeepFormatButton += '</button>';

        const html = '<div class="fr-image-variable-layer fr-layer fr-active fr-buttons m-0 w-auto"><div class="d-flex align-items-center">'
          .concat(cleanPasteButton).concat(pasteKeepFormatButton).concat('</div>');

        // Load popup template.
        template = {
          buttons: popup_buttons,
          custom_layer: html,
        };

        FroalaEditor.RegisterCommand('pasteCleanButton', {
          title: 'Paste Clean',
          undo: true,
          focus: false,
          callback(cmd) {
            const pasteData = FroalaEditor.PASTE_DATA;
            const content = pasteData['text/plain'];
            this.html.insert(content.replace(/<[^>]*>?/g, ''), true);
            hidePopup();
          }
        });

        FroalaEditor.RegisterCommand('pasteKeepButton', {
          title: 'Paste With Formatting',
          undo: true,
          focus: false,
          callback(cmd) {
            const pasteData = FroalaEditor.PASTE_DATA;
            let content = '';
            if (pasteData['text/html']) {
              content = pasteData['text/html'];
            } else {
              content = pasteData['text/plain'];
            }
            this.html.insert(content, false);
            hidePopup();
          }
        });

        return editor.popups.create(customPopupName, template);
      }

      return {
        _init: function _init() {
          editor.events.on('paste.before', function(e) {
            return showPopup(e);
          });
        },
        showPopup,
        hidePopup,
        back: function back() {
          editor.events.disableBlur();
          editor.selection.restore();
          editor.popups.hide(customPopupName);
        }
      };
    };

    // Define custom popup close button icon and command.
    FroalaEditor.RegisterCommand('pastePopupClose', {
      title: 'Close',
      icon: 'imageBack',
      undo: false,
      focus: false,
      callback() {
        this.customPaste.back();
      }
    });
  }

  customImageVariableOptions() {
    const that = this;
    const customPopupName = 'imageVariable.popup';
    const customPlugin = 'imageVariable';

    // Define popup template.
    Object.assign(FroalaEditor.POPUP_TEMPLATES, {
      [customPopupName]: '[_BUTTONS_][_CUSTOM_LAYER_]'
    });

    // Define popup buttons.
    Object.assign(FroalaEditor.DEFAULTS, {
      popupButtons: ['popupClose', '-']
    });

    // The custom popup is defined inside a plugin (new or existing).
    FroalaEditor.PLUGINS[customPlugin] = function(editor) {
      let imageSrcOptions = {
        none: [{name: 'None', key: '', valueType: ['text', 'all']}],
      };
      const customVariables = that.customVariables();
      if (customVariables) {
        imageSrcOptions = {
          none: [{name: 'None', key: '', valueType: ['text', 'all']}],
          ...customVariables
        };
      }
      // Create custom popup.
      function initPopup() {
        // Load popup template.
        let template = FroalaEditor.POPUP_TEMPLATES[customPopupName];
        if (typeof template === 'function') {
          template = template.apply(editor);
        }

        // Popup buttons.
        let popup_buttons = '';

        // Create the list of buttons.
        if (editor.opts.popupButtons.length > 1) {
          popup_buttons += '<div class="fr-buttons fr-tabs">';
          popup_buttons += editor.button.buildList(editor.opts.popupButtons);
          popup_buttons += '</div>';
        }

        // Define Popup Template - icon when empty, icon + text + clear when selected
        let pickerButton = '<div class="imageSrcVariablePicker" style="display:flex;align-items:center;cursor:pointer;padding:4px 8px;" title="Select Variable">';
        pickerButton += '<span class="imageSrcVariableIcon" style="display:inline-flex;width:20px;height:20px;">' + editor.icon.create('variable') + '</span>';
        pickerButton += '<span class="imageSrcVariableLabel d-none" style="margin-left:4px;font-size:13px;white-space:nowrap;color:#333;"></span>';
        pickerButton += '<span class="imageSrcVariableClear d-none" style="cursor:pointer;color:#999;font-size:16px;margin-left:4px;line-height:1;" title="Clear">&times;</span>';
        pickerButton += '</div>';

        let checkBoxHtml = '<span style="text-align: left"><div class="fr-checkbox-line fr-autoplay-margin imageSrcVariableCheck">';
        checkBoxHtml += '<span class="fr-checkbox"><input id="imageSrcVariableCheck-' + editor.id + '" data-cmd="imageSrcVariableCheck" data-param1="true" class="fr-command" type="checkbox" dir="auto" value/><span><svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="10" height="10" viewBox="0 0 32 32"><path d="M27 4l-15 15-7-7-5 5 12 12 20-20z" fill="#FFF"></path></svg></span></span>';
        checkBoxHtml += '<label style="line-height: 1;white-space: break-spaces;word-break: break-word;"' +
          ' id="imageSrcVariableCheckLabel-' + editor.id + '" data-cmd="imageSrcVariableCheck" data-param1="true"' +
          ' class="fr-command">Show the uploaded image when the selected dynamic image is not available</label></div></span>';

        const html = '<div class="fr-image-variable-layer fr-layer fr-active m-0 w-auto">' +
          '<div class="d-flex align-items-center">'.concat(pickerButton).concat(checkBoxHtml, '</div>').concat('</div>');
        // Load popup template.
        template = {
          buttons: popup_buttons,
          custom_layer: html,
        };

        // Define custom popup template checkbox command
        FroalaEditor.RegisterCommand('imageSrcVariableCheck', {
          undo: false,
          focus: false,
          callback(cmd, val) {
            let img = null;
            if (this.el instanceof HTMLImageElement) {
              img = this.el;
            } else {
              img = this.el.querySelector('img');
            }
            if (img.getAttribute('data-src-visibility')) {
              img.removeAttribute('data-src-visibility');
            } else {
              img.setAttribute('data-src-visibility', val);
            }
          }
        });

        // Create popup.
        const $popup = editor.popups.create(customPopupName, template);
        const popupEl = $popup[0] || $popup;

        // Attach click handler to the picker container using native DOM
        const pickerEl = popupEl.querySelector('.imageSrcVariablePicker');
        if (pickerEl) {
          pickerEl.addEventListener('click', function(e) {
            if (e.target.closest('.imageSrcVariableClear')) {
              // Clear the variable
              e.stopPropagation();
              let img = null;
              if (editor.el instanceof HTMLImageElement) {
                img = editor.el;
              } else {
                img = editor.el.querySelector('img');
              }
              if (img) {
                img.removeAttribute('data-src-variable');
              }
              const lbl = popupEl.querySelector('.imageSrcVariableLabel');
              const clr = popupEl.querySelector('.imageSrcVariableClear');
              if (lbl) {
                lbl.textContent = ''; lbl.classList.add('d-none');
              }
              if (clr) {
                clr.classList.add('d-none');
              }
            } else {
              // Open the variable picker - save img reference before toolbar closes
              let img = null;
              if (editor.el instanceof HTMLImageElement) {
                img = editor.el;
              } else {
                img = editor.el.querySelector('img');
              }
              that._pendingImageElement = img;
              that._pendingImagePopupEl = popupEl;
              that.openVariablePicker('image');
            }
          });
        }

        return $popup;
      }

      // Set default value according to attributes (using native DOM API)
      function checkElementForData(instance, popup) {
        const popupEl = popup[0] || popup;
        let img = null;
        if (instance.el instanceof HTMLImageElement) {
          img = instance.el;
        } else {
          img = instance.el.querySelector('img');
        }
        if (!img) {
          return;
        }
        let value = img.getAttribute('data-src-visibility');
        const checkboxEl = popupEl.querySelector('.imageSrcVariableCheck input');
        if (checkboxEl) {
          checkboxEl.checked = !!value;
        }

        value = img.getAttribute('data-src-variable');
        const labelEl = popupEl.querySelector('.imageSrcVariableLabel');
        const clearEl = popupEl.querySelector('.imageSrcVariableClear');
        if (!labelEl || !clearEl) {
          return;
        }
        if (value) {
          let newOptions = [];
          Object.keys(imageSrcOptions).forEach(group => {
            newOptions = [...newOptions, ...imageSrcOptions[group]];
          });
          const option = newOptions.find(item => item.key === value);
          if (option) {
            labelEl.textContent = option.name;
            labelEl.classList.remove('d-none');
            clearEl.classList.remove('d-none');
          }
        } else {
          labelEl.textContent = '';
          labelEl.classList.add('d-none');
          clearEl.classList.add('d-none');
        }
      }

      // Show the popup
      function showPopup() {
        let $popup = editor.popups.get(customPopupName);
        if (!$popup) {
          $popup = initPopup();
        }

        editor.popups.onShow(customPopupName, function() {
          checkElementForData(this, $popup);
        });

        editor.popups.setContainer(customPopupName, editor.$sc);
        const $btn = editor.$sc.find('.fr-active .fr-command[data-cmd="imageVariableButton"]');
        const left = $btn.offset().left;
        const top = $btn.offset().top - (editor.opts.toolbarBottom ? 10 : ($btn.outerHeight() - 10));
        editor.popups.show(customPopupName, left, top, $btn.outerHeight());
      }

      // Hide the custom popup.
      function hidePopup() {
        editor.popups.hide(customPopupName);
      }

      // Methods visible outside the plugin.
      return {
        showPopup,
        hidePopup
      };
    };

    // Define custom popup close button icon and command.
    FroalaEditor.RegisterCommand('popupClose', {
      title: 'Close',
      icon: 'imageBack',
      undo: false,
      focus: false,
      callback() {
        this.image.back();
      }
    });

    // Define an icon and command for the button that opens the custom popup.
    FroalaEditor.DefineIcon('imageVariableButton', { NAME: 'cog', template: 'font_awesome'});
    FroalaEditor.RegisterCommand('imageVariableButton', {
      title: 'Use Variable',
      icon: 'imageVariableButton',
      undo: false,
      focus: false,
      popup: true,
      plugin: 'imageVariable',
      callback() {
        if (!this.popups.isVisible(customPopupName)) {
          this[customPlugin].showPopup();
        } else {
          if (this.$el.find('.fr-marker')) {
            this.events.disableBlur();
            this.selection.restore();
          }
          this[customPlugin].hidePopup();
        }
      }
    });
  }

  addCustomVariables() {
    const that = this;
    FroalaEditor.DefineIcon('variable', {NAME: 'users', template: 'font_awesome'});
    FroalaEditor.RegisterCommand('variable', {
      title: 'Variables',
      icon: 'variable',
      focus: false,
      undo: false,
      callback(cmd) {
        this.selection.save();
        that._pendingEditor = this;
        that.openVariablePicker('text');
      }
    });

    FroalaEditor.RegisterCommand('linkUrlVariables', {
      title: 'URL Variables',
      icon: 'imageLink',
      focus: false,
      undo: false,
      refreshAfterCallback: false,
      callback(cmd) {
        that._pendingEditor = this;
        that.openVariablePicker('linkUrl');
      },
      plugin: 'link'
    });

    FroalaEditor.RegisterCommand('linkTextVariables', {
      title: 'Text Variables',
      icon: 'moreText',
      focus: false,
      undo: false,
      refreshAfterCallback: false,
      callback(cmd) {
        that._pendingEditor = this;
        that.openVariablePicker('linkText');
      },
      plugin: 'link'
    });

    this.customImageVariableOptions();
    this.customPaste();
    this.customFontFamily();
    this.customFontSize();
  }

  public ngAfterViewInit() {
    this._messageListener = this.receiveMessage.bind(this);
    window.addEventListener('message', this._messageListener, false);
    this.initEditor();
  }

  initEditor() {
    const that = this;
    this.addCustomVariables();

    const editorOptions = {
      key: environment.froalaEditorKey,
      toolbarInline: true,
      toolbarVisibleWithoutSelection: false,
      htmlUntouched: true,
      attribution: false,
      charCounterCount: false,
      imageInsertButtons: ['imageBack', '|', 'imageUpload', 'imageByURL'],
      imageUploadURL: environment.apiUrl + '/api/v1/file',
      fileUploadURL: environment.apiUrl + '/api/v1/file',
      requestHeaders: {
        Authorization: `Bearer ${this.token}`
      },
      events: {
        initialized() {
          that.editorLoaded = true;
          that.isEditorLoaded.emit(true);
        },
        contentChanged() {
          that.onEditorChange();
        },
        'image.uploaded'(response) {
          const jsonResponse = JSON.parse(response);
          this.image.insert(jsonResponse.data.url, false, null, this.image.get(), response);
          return false;
        },
        'file.uploaded'(response) {
          const jsonResponse = JSON.parse(response);
          this.file.insert(jsonResponse.data.url, jsonResponse.data.file, null);
          return false;
        },
        'paste.before'(e) {
          const pasteData = {};
          const types = e.clipboardData.types;
          const isLinux = /Linux/.test(navigator.platform) || /Linux/.test(navigator.userAgent);
          for (const type of types) {
            if (type !== 'text/html') {
              pasteData[type] = e.clipboardData.getData(type);
            } else {
              let pasteContent = e.clipboardData.getData(type);
              pasteContent = pasteContent.replace(/https?:\/\/[^"]*?(#\w+#)/g, '$1');
              if (this.helpers.isIOS() || this.helpers.isMac() || isLinux) {
                pasteData[type] = pasteContent;
              } else {
                pasteData[type] = pasteContent.match(/(StartFragment-->)(.*?<!--EndFragment)/g);
                if (pasteData[type] && pasteData[type][0]) {
                  pasteData[type] = pasteData[type][0].replace('StartFragment-->', '').replace('<!--EndFragment', '');
                }
              }
            }
          }
          Object.assign(FroalaEditor, {
            PASTE_DATA: pasteData
          });
          return false;
        },
        mousedown(mousedownEvent) {
          Object.assign(FroalaEditor, {
            SELECTED_TARGET: mousedownEvent.target
          });
        }
      },
      linkInsertButtons: ['linkBack', '|', 'linkList', 'linkUrlVariables', 'linkTextVariables'],
      pluginsEnabled: ['align', 'charCounter', 'codeBeautifier', 'codeView', 'colors', 'cryptoJSPlugin', 'draggable', 'emoticons', 'entities',
        'file', 'filesManager', 'fontAwesome', 'fontFamily', 'fontSize', 'forms', 'fullscreen', 'help', 'image', 'imageManager', 'imageVariable',
        'inlineClass', 'inlineStyle', 'lineBreaker', 'lineHeight', 'link', 'lists', 'markdown', 'paragraphFormat', 'paragraphStyle',
        'print', 'quickInsert', 'quote', 'save', 'specialCharacters', 'table', 'url', 'video', 'wordPaste', 'customPaste'],
      imageEditButtons: ['imageReplace', 'imageVariableButton', 'imageAlign', 'imageCaption', 'imageRemove', 'imageLink', 'linkOpen',
        'linkEdit', 'linkRemove', '-', 'imageDisplay', 'imageStyle', 'imageAlt', 'imageSize'],
      toolbarButtons: ['variable', '|', 'bold', 'italic', 'textColor', 'backgroundColor', 'paragraphFormat', 'align', 'emoticons', 'insertLink',
        'insertImage', 'undo', 'redo', '-', 'underline', 'subscript', 'superscript', 'fontSize', 'fontFamily', 'insertTable', 'formatOL', 'formatUL',
        'lineHeight', 'insertVideo', 'fontAwesome', 'inlineClass', 'inlineStyle', 'insertFile']
    };
    let editorsIds = Object.keys(this.editables).map(i => '#' + i);
    editorsIds.forEach(editor => {

      const edit = new FroalaEditor(editor, editorOptions);
      this.editors.push(edit);
    });
    editorsIds = Object.keys(this.imageEditables).map(i => '#' + i);
    editorsIds.forEach(editor => {

      const edit = new FroalaEditor(editor, {
        key: environment.froalaEditorKey,
        toolbarInline: true,
        toolbarVisibleWithoutSelection: false,
        attribution: false,
        htmlUntouched: true,
        charCounterCount: false,
        imageInsertButtons: ['imageBack', '|', 'imageUpload', 'imageByURL'],
        pluginsEnabled: ['align', 'charCounter', 'codeBeautifier', 'codeView', 'colors', 'cryptoJSPlugin', 'draggable', 'emoticons', 'entities',
          'file', 'filesManager', 'fontAwesome', 'fontFamily', 'fontSize', 'forms', 'fullscreen', 'help', 'image', 'imageManager', 'imageVariable',
          'inlineClass', 'inlineStyle', 'lineBreaker', 'lineHeight', 'link', 'lists', 'markdown', 'paragraphFormat', 'paragraphStyle', 'print',
          'quickInsert', 'quote', 'save', 'specialCharacters', 'table', 'url', 'video', 'wordPaste'],
        imageEditButtons: ['imageReplace', 'imageVariableButton', 'imageAlign', 'imageCaption', 'imageRemove', 'imageLink', 'linkOpen',
          'linkEdit', 'linkRemove', '-', 'imageDisplay', 'imageStyle', 'imageAlt', 'imageSize'],
        imageUploadURL: environment.apiUrl + '/api/v1/file',
        requestHeaders: {
          Authorization: `Bearer ${this.token}`
        },
        events: {
          initialized() {
            that.editorLoaded = true;
            that.isEditorLoaded.emit(true);
          },
          'image.uploaded'(response) {
            const jsonResponse = JSON.parse(response);
            this.image.insert(jsonResponse.data.url, false, null, this.image.get(), response);
            return false;
          }
        }
      });
      this.imageEditors.push(edit);
    });
  }

  initEditables() {
    this.HTMLTemplate = document.createElement('div');
    this.HTMLTemplate.innerHTML = this.template()['body'];

    let elements = this.HTMLTemplate.getElementsByClassName('editable');
    for (let i = 0; i < elements.length; i++) {
      const id = `editor${i + 1}`;
      elements[i].setAttribute('id', id);
      this.editables[id] = elements[i];
    }

    elements = this.HTMLTemplate.getElementsByClassName('mcnImage');
    for (let i = 0; i < elements.length; i++) {
      const id = `image-editor${i + 1}`;
      elements[i].setAttribute('id', id);
      this.imageEditables[id] = elements[i];
    }
  }

  onEditorChange() {
    const templateValue = this.template();
    if (templateValue && this.editorLoaded) {
      const parser = new DOMParser();
      const templateDocument = parser.parseFromString(templateValue.body, 'text/html');
      const templateEdit = document.getElementById('editor1');
      const elm: any = templateEdit.querySelectorAll('a:not(.mcnButton)');
      if (elm) {
        for (const link of elm) {
          link.style.color = this.linkColor;
        }
      }
      templateValue.body = templateEdit.innerHTML;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        const template = this.buildUpdatedTemplate();
        if (template) {
          this.onChange()(template);
        }
      }, 1000);
      const templateListSpacing = this.templateListSpacing();
      if (templateListSpacing) {
        this.replaceListSpacing(templateListSpacing);
      }
    }
  }

  buildUpdatedTemplate() {
    let html: any = document.getElementById('email-editor');
    if (html) {
      html = html.cloneNode(true);
      const editables = Object.keys(this.editables);
      for (let i = 0; i < editables.length; i++) {
        const getEditableContent = html.querySelector(`#${editables[i]}`);
        getEditableContent.innerHTML = this.editors[i].html.get(true);

        const removePoweredBy = html.querySelector(`p[data-f-id^="pbf"]`);
        if (removePoweredBy) {
          removePoweredBy.remove();
        }
      }
      return {body: html.innerHTML};
    }
    return null;
  }

  replaceButtonText(text) {
    if (this.template() && this.editorLoaded) {
      const editor = document.getElementById('email-editor');
      const buttonElement: any = editor.querySelector('.main-button');
      if (buttonElement) {
        buttonElement.innerHTML = text;
        buttonElement.title = text;
        this.onEditorChange();
      }
    } else {
      setTimeout(() => {
        this.replaceButtonText(text);
      }, 1000);
    }
  }

  replaceButtonTextColor(color) {
    if (this.template() && this.editorLoaded) {
      const editor = document.getElementById('email-editor');
      const buttonElement: any = editor.querySelector('.main-button');
      if (buttonElement) {
        buttonElement.style.color = color;
        this.onEditorChange();
      }
    } else {
      setTimeout(() => {
        this.replaceButtonTextColor(color);
      }, 1000);
    }
  }

  replaceButtonLink(link) {
    if (this.template() && this.editorLoaded) {
      const editor = document.getElementById('email-editor');
      const buttonElement: any = editor.querySelector('.main-button');
      if (buttonElement) {
        buttonElement.href = link;
        this.onEditorChange();
      }
    } else {
      setTimeout(() => {
        this.replaceButtonLink(link);
      }, 1000);
    }
  }

  replaceButtonColor(color) {
    if (this.template() && this.editorLoaded) {
      const editor = document.getElementById('email-editor');
      const buttonElement: any = editor.querySelector('.main-button');
      if (buttonElement) {
        buttonElement.parentNode.parentNode.parentNode.parentNode.style.backgroundColor = color;
        this.onEditorChange();
      }
    } else {
      setTimeout(() => {
        this.replaceButtonColor(color);
      }, 1000);
    }
  }

  addCSSRule(sheet, selector, rules, index) {
    if('insertRule' in sheet) {
      sheet.insertRule(selector + '{' + rules + '}', index);
    } else if('addRule' in sheet) {
      sheet.addRule(selector, rules, index);
    }
  }

  removeCSSRule(sheet, selector, rules, index) {
    if('deleteRule' in sheet) {
      sheet.deleteRule(index);
    } else if('removeRule' in sheet) {
      sheet.removeRule(index);
    }
  }

  CCSStylesheetRuleStyle(document, stylesheet, selectorText, style, value, important) {
    /* returns the value of the element style of the rule in the stylesheet
    *  If no value is given, reads the value
    *  If value is given, the value is changed and returned
    *  If '' (empty string) is given, erases the value.
    *  The browser will apply the default one
    *
    * string stylesheet: part of the .css name to be recognized, e.g. 'default'
    * string selectorText: css selector, e.g. '#myId', '.myClass', 'thead td'
    * string style: camelCase element style, e.g. 'fontSize'
    * string value optional : the new value
    */
    let sheet = null;
    let CSSstyle; let rules;
    for (const m of Object.keys(document.styleSheets)) {
      if (document.styleSheets[m].ownerNode && document.styleSheets[m].ownerNode.getAttribute('id') === 'CCSStylesheetRuleStyle') {
        sheet = document.styleSheets[m];
        break;
      }
    }

    if (!sheet) {
      const style = document.createElement('style');
      style.setAttribute('id', 'CCSStylesheetRuleStyle');
      document.head.appendChild(style); // must append before you can access sheet property
      sheet = style.sheet;
      this.addCSSRule(sheet, 'body', '', 0);
      // this.addCSSRule(sheet, "h1, h2, h3, h4, h5, h6", "", 0);
      this.addCSSRule(sheet, 'h1', '', 0);
      this.addCSSRule(sheet, 'h2', '', 0);
      this.addCSSRule(sheet, 'h3', '', 0);
      this.addCSSRule(sheet, 'h4', '', 0);
      this.addCSSRule(sheet, 'h5', '', 0);
      this.addCSSRule(sheet, 'h6', '', 0);
      this.addCSSRule(sheet, 'a', '', 0);
      this.addCSSRule(sheet, 'a.btn', '', 0);
      this.addCSSRule(sheet, 'a.btn.btn-secondary', '', 0);
      this.addCSSRule(sheet, 'a.btn:hover', '', 0);
      this.addCSSRule(sheet, 'a.btn.btn-secondary:hover', '', 0);
      this.addCSSRule(sheet, 'p', '', 0);
      this.addCSSRule(sheet, 'button','', 0);
      this.addCSSRule(sheet, 'button:hover', '', 0);
      this.addCSSRule(sheet, 'section.fdb-block,header,footer','', 0);
    }
    const styleDashed = style.replace(/[A-Z]/g, m => '-' + m.toLowerCase());
    if (sheet) {
      rules = sheet[document.all ? 'rules' : 'cssRules'];
      for (const n of Object.keys(rules)) {
        if (rules[n].selectorText === selectorText) {
          CSSstyle = rules[n].style;
          break;
        }
      }
    }

    if (CSSstyle) {
      if (value === undefined || value === '' || value === null) {
        CSSstyle.removeProperty(styleDashed);
        CSSstyle.removeProperty(style);
      } else {
        if (important) {
          CSSstyle.setProperty(styleDashed, value, 'important');
          return CSSstyle.setProperty(style, value, 'important');
        } else {
          CSSstyle[styleDashed] = value;
          return CSSstyle[style] = value;
        }
      }
    }
  }

  replaceLinkColor(color) {
    this.linkColor = color;
    if (this.template() && this.editorLoaded) {
      const editorBody = document.getElementById('templateBody');
      const elm: any = editorBody.querySelectorAll('a:not(.mcnButton)');
      if (elm) {
        for (const link of elm) {
          link.style.color = this.linkColor;
          this.onEditorChange();
        }
      }
    } else {
      setTimeout(() => {
        this.replaceLinkColor(this.linkColor);
      }, 1000);
    }
  }

  replaceLinkHoverColor(color) {
    if (this.template() && this.editorLoaded) {
      const editor = document.getElementById('email-editor');
      this.CCSStylesheetRuleStyle(editor, 'editor', 'a:hover', 'color', color, null);
    } else {
      setTimeout(() => {
        this.replaceLinkHoverColor(color);
      }, 1000);
    }
  }

  replaceBackGroundColor(color) {
    if (this.template() && this.editorLoaded) {
      const editor = document.getElementById('email-editor');
      const bodyTable: any = editor.querySelector('#bodyTable');
      const editorHeader: any = editor.querySelector('#templatePreheader');
      const buttonEditable: any = editor.querySelector('.btn-editable');
      // const templateFooter: any = editor.querySelector('#templateFooter');
      if (bodyTable) {
        bodyTable.style.background = color;
        this.onEditorChange();
      }
      if (editorHeader) {
        editorHeader.style.background = color;
        this.onEditorChange();
      }
      if (buttonEditable) {
        buttonEditable.style.background = color;
        this.onEditorChange();
      }
      // if (templateFooter) {
      //   templateFooter.style.background = color;
      //   this.onEditorChange();
      // }
    } else {
      setTimeout(() => {
        this.replaceBackGroundColor(color);
      }, 1000);
    }
  }

  replaceButtonSpacing(space) {
    if (this.template() && this.editorLoaded) {
      const editor = document.getElementById('email-editor');
      const buttonElement: any = editor.querySelector('.main-button');
      if (buttonElement && space !== '') {
        buttonElement.style.margin = `0 ${space || 0}px`;
        this.onEditorChange();
      }
    } else {
      setTimeout(() => {
        this.replaceButtonSpacing(space);
      }, 1000);
    }
  }

  replaceParagraphSpacing(space) {
    if (this.template() && this.editorLoaded) {
      const editor = document.getElementById('email-editor');
      const paragraphElement: any = editor.querySelectorAll('p');
      if (paragraphElement && space !== '') {
        for (const paragraph of paragraphElement) {
          paragraph.style.marginBottom = `${space || 0}px`;
        }
        this.onEditorChange();
      }
    } else {
      setTimeout(() => {
        this.replaceParagraphSpacing(space);
      }, 1000);
    }
  }

  replaceListSpacing(space) {
    if (this.template() && this.editorLoaded) {
      const editor = document.getElementById('email-editor');
      const listElement: any = editor.querySelectorAll('li');
      if (listElement && space !== '') {
        for (const listItem of listElement) {
          listItem.style.marginBottom = `${space || 0}px`;
        }
      }
    } else {
      setTimeout(() => {
        this.replaceListSpacing(space);
      }, 1000);
    }
  }

  receiveMessage(event: MessageEvent): void {
    if (event.data && event.data.type === 'variable' && event.data.target === 'froala_variable_picker') {
      this.handleVariableSelected(event.data.data);
    }
  }

  openVariablePicker(context: string): void {
    this._pendingVariableContext = context;
    const pagePath = window.location.pathname + window.location.hash;
    const fieldName = 'froala_editor';
    const fieldTypeMap = { text: 'text', linkUrl: 'url', linkText: 'text', image: 'image', video: 'video' };
    const fieldType = fieldTypeMap[context] || 'text';
    const baseUrl = environment.frontUrl.replace('/#', '').replace(/\/+$/, '');
    const theme = document.body.classList.contains('dark-theme') ? 'dark' : 'light';
    const url = `${baseUrl}/b/variable/template?type=variable&target=froala_variable_picker&field_name=${encodeURIComponent(fieldName)}&field_type=${encodeURIComponent(fieldType)}&format=html&page_path=${encodeURIComponent(pagePath)}&theme=${theme}`;

    setTimeout(() => {
      this.variablePickerUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
    });

    const modalRef = this._modalService.open(this.variablePickerModal(), {
      centered: false,
      size: 'xl',
      keyboard: true,
      windowClass: 'snoball-iframe-modal-window'
    });
    modalRef.result.then(
      () => {
        this.variablePickerUrl.set(null);
      },
      () => {
        this.variablePickerUrl.set(null);
      }
    );
  }

  handleVariableSelected(variable: any): void {
    const context = this._pendingVariableContext;
    const editor = this._pendingEditor;
    const key = typeof variable === 'string' ? variable : variable.key;
    const name = typeof variable === 'string' ? variable : (variable.name || variable.key);

    if (context === 'image') {
      const img = this._pendingImageElement;
      const popupEl = this._pendingImagePopupEl;
      if (img && key && key.trim()) {
        img.setAttribute('data-src-variable', key);
        if (popupEl) {
          const labelEl = popupEl.querySelector('.imageSrcVariableLabel');
          const clearEl = popupEl.querySelector('.imageSrcVariableClear');
          if (labelEl) {
            labelEl.textContent = name || key; labelEl.classList.remove('d-none');
          }
          if (clearEl) {
            clearEl.classList.remove('d-none');
          }
        }
      } else if (img) {
        img.removeAttribute('data-src-variable');
        if (popupEl) {
          const labelEl = popupEl.querySelector('.imageSrcVariableLabel');
          const clearEl = popupEl.querySelector('.imageSrcVariableClear');
          if (labelEl) {
            labelEl.textContent = ''; labelEl.classList.add('d-none');
          }
          if (clearEl) {
            clearEl.classList.add('d-none');
          }
        }
      }
      this._pendingImageElement = null;
      this._pendingImagePopupEl = null;
    } else if (context === 'text' && editor) {
      editor.selection.restore();
      if (key === '#InfluencerSessionsList#') {
        editor.html.insert(`<div>${key}</div>`);
      } else {
        editor.html.insert(key);
      }
    } else if (context === 'linkUrl' && editor) {
      const $popup = editor.popups.get('link.insert');
      const urlInput = $popup.find(`#fr-link-insert-layer-url-${editor.id}`);
      if (urlInput && urlInput[0]) {
        urlInput[0].value = key;
        urlInput[0].focus();
      }
    } else if (context === 'linkText' && editor) {
      const $popup = editor.popups.get('link.insert');
      const textInput = $popup.find(`#fr-link-insert-layer-text-${editor.id}`);
      if (textInput && textInput[0]) {
        textInput[0].value = key;
        textInput[0].focus();
      }
    }

    this._pendingVariableContext = null;
    this._pendingEditor = null;
    this._modalService.dismissAll();
  }

  ngOnDestroy() {
    if (this._messageListener) {
      window.removeEventListener('message', this._messageListener, false);
    }
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }
}
